import type { BlogPost } from '../../../shared/models/blog.model';

/**
 * Zona de la tienda. Explícita en el formateador para que el render SSR (que
 * corre con la zona del servidor) y el del navegador pinten el mismo día —
 * mismo criterio que `STORE_TIMEZONE` en `events/utils/event-format.util.ts`.
 */
export const BLOG_TIMEZONE = 'America/Costa_Rica';

/**
 * Opciones del formateador de `publishedAt`, exportadas para que el spec
 * congele `timeZone`: Karma corre con la zona del equipo (Costa Rica), así que
 * sin esa aserción quitar la zona no rompería ningún test. `tools/check-blog-timezone.mjs`
 * (`npm run tz:check`) lo comprueba además con el proceso en UTC.
 */
export const BLOG_DATE_FORMAT_OPTIONS: Readonly<Intl.DateTimeFormatOptions> = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: BLOG_TIMEZONE,
};

const PUBLISHED_AT_FORMATTER = new Intl.DateTimeFormat('es-CR', BLOG_DATE_FORMAT_OPTIONS);

/**
 * Fecha de publicación para pintar (B16: la API emite ISO 8601 y el cliente
 * formatea). Una fecha que no se puede leer devuelve cadena vacía antes que
 * «Invalid Date».
 */
export function formatBlogDate(publishedAt: string): string {
  const date = new Date(publishedAt);
  return Number.isNaN(date.getTime()) ? '' : PUBLISHED_AT_FORMATTER.format(date);
}

/**
 * Orden canónico del blog (B49): `publishedAt` descendente y desempate por
 * `id` descendente, para que dos posts con la misma fecha no bailen entre
 * renders.
 */
export function compareBlogPostsByPublishedAtDesc(a: BlogPost, b: BlogPost): number {
  const byDate = new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  if (byDate !== 0) return byDate;
  return b.id < a.id ? -1 : b.id > a.id ? 1 : 0;
}
